/**
 * useCodeExecution Hook - Code execution via streaming API
 *
 * This hook handles:
 * - Calling the /api/boards/:boardId/execute endpoint
 * - Parsing Server-Sent Events (SSE) for real-time streaming
 * - Managing execution state (idle, running, complete, error)
 * - Providing callbacks for stdout/stderr/result
 * - Cancellation support via AbortController
 *
 * Usage:
 * ```typescript
 * const { execute, cancel, status, error } = useCodeExecution({
 *   boardId: 'board-123',
 *   onStdout: (data) => terminal.write(data),
 *   onStderr: (data) => terminal.write(data),
 *   onResult: (result) => handleResult(result),
 * });
 *
 * // Execute code
 * await execute('print("Hello")', 'python');
 *
 * // Cancel running execution
 * cancel();
 * ```
 */

import { useState, useCallback, useRef } from 'react';
import type { ExecutionResult, ExecutionStatus, CodeLanguage } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Options for the useCodeExecution hook
 */
interface UseCodeExecutionOptions {
  /** Board ID - used to scope the sandbox session */
  boardId: string;

  /** Callback when stdout data is received (streaming) */
  onStdout?: (data: string) => void;

  /** Callback when stderr data is received (streaming) */
  onStderr?: (data: string) => void;

  /** Callback when execution completes with result */
  onResult?: (result: ExecutionResult) => void;
}

/**
 * Return type of the useCodeExecution hook
 */
interface UseCodeExecutionReturn {
  /** Execute code with the given language */
  execute: (code: string, language: CodeLanguage) => Promise<void>;

  /** Cancel the current execution */
  cancel: () => void;

  /** Current execution status */
  status: ExecutionStatus;

  /** Error message if status is 'error' */
  error: string | null;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for executing code via the streaming API
 *
 * Manages the full lifecycle of code execution:
 * 1. Sends POST request to /api/boards/:boardId/execute
 * 2. Reads SSE stream for stdout/stderr/result events
 * 3. Updates status and triggers callbacks
 * 4. Supports cancellation
 */
export function useCodeExecution({
  boardId,
  onStdout,
  onStderr,
  onResult,
}: UseCodeExecutionOptions): UseCodeExecutionReturn {
  // Execution state
  const [status, setStatus] = useState<ExecutionStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Abort controller for cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Execute code in the sandbox
   *
   * @param code - The code to execute
   * @param language - Programming language ('python' or 'javascript')
   */
  const execute = useCallback(async (code: string, language: CodeLanguage) => {
    // Abort any existing execution
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this execution
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Update state to running
    setStatus('running');
    setError(null);

    try {
      // POST to execute endpoint
      const response = await fetch(`/api/boards/${boardId}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, language }),
        signal: abortController.signal,
      });

      // Handle HTTP errors
      if (!response.ok) {
        throw new Error(`Execution failed: ${response.statusText}`);
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      // Process stream chunks
      while (true) {
        const { done, value } = await reader.read();

        // Stream complete
        if (done) break;

        // Decode chunk to text
        const text = decoder.decode(value);

        // SSE format: each event is "data: {json}\n\n"
        const lines = text.split('\n');

        for (const line of lines) {
          // Skip non-data lines
          if (!line.startsWith('data: ')) continue;

          // Extract JSON payload
          const data = line.slice(6); // Remove "data: " prefix

          // Handle stream completion marker
          if (data === '[DONE]') {
            setStatus('complete');
            break;
          }

          try {
            // Parse SSE event
            const event = JSON.parse(data);

            // Route event to appropriate callback
            if (event.type === 'stdout') {
              onStdout?.(event.data);
            }

            if (event.type === 'stderr') {
              onStderr?.(event.data);
            }

            if (event.type === 'complete') {
              onResult?.(event.result);
              setStatus('complete');
            }

            if (event.type === 'error') {
              setError(event.error);
              setStatus('error');
            }
          } catch {
            // Ignore parse errors for partial/incomplete messages
            // SSE chunks may split across boundaries
          }
        }
      }
    } catch (err) {
      // Handle cancellation (not an error)
      if (err instanceof Error && err.name === 'AbortError') {
        setStatus('idle');
        return;
      }

      // Handle actual errors
      setError(err instanceof Error ? err.message : 'Execution failed');
      setStatus('error');
    }
  }, [boardId, onStdout, onStderr, onResult]);

  /**
   * Cancel the current execution
   */
  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setStatus('idle');
  }, []);

  return { execute, cancel, status, error };
}
